import { useMemo, useState } from "react";
import { AlertCircle, Loader2 } from "@digital-twins/design-system/icons";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { AnalystElapsed } from "./AnalystElapsed";
import { buildFactNumbering } from "./analystFactNumbering";
import { AnalystChart } from "./AnalystChart";
import { AnalystEvidence } from "./AnalystEvidence";
import { AnalystTable } from "./AnalystTable";
import { SafeAnalystMarkdown } from "./SafeAnalystMarkdown";

import {
  analystFailureCopyKey,
  isTerminalAnalystStatus,
} from "@digital-twins/shared-types";

import type { PersistedAnalystMessage } from "@digital-twins/shared-types";

interface AnalystMessageProps {
  message: PersistedAnalystMessage;
  onRetry: (requestToken: string | null) => void;
}

export function AnalystMessage(props: AnalystMessageProps) {
  const { message, onRetry } = props;
  const { t } = useTranslation("app");

  // Controlled evidence-disclosure state, lifted here (not into
  // AnalystEvidence) because a fact chip in SafeAnalystMarkdown must be able
  // to open the disclosure and scroll a specific row into view. See
  // docs/plans/analyst-thread-alignment.md, Step 4 "Controlled open state".
  const [isEvidenceOpen, setIsEvidenceOpen] = useState(false);
  const [focusFactId, setFocusFactId] = useState<string | null>(null);

  const tables = message.tables ?? [];
  const isPending = !isTerminalAnalystStatus(message.status);
  const isFailed = message.status === "failed";
  const isRefused = message.status === "refused";
  // The reply's typed kind, never its wording.
  const isClarification = message.responseKind === "clarification";

  // Frozen contract (docs/plans/analyst-thread-alignment.md, "Frozen
  // contract"): built once per message and handed to both
  // SafeAnalystMarkdown and AnalystEvidence, so a fact chip and its
  // disclosure row can never disagree on a number.
  const numbering = useMemo(
    () => buildFactNumbering(message.content, message.evidence),
    [message.content, message.evidence],
  );
  // A known fact's chip is the shared source pill, whose card shows the fact.
  const factsById = useMemo(
    () => new Map(message.evidence.map((fact) => [fact.factId, fact])),
    [message.evidence],
  );

  function handleOpenFact(factId: string) {
    setIsEvidenceOpen(true);
    setFocusFactId(factId);
  }

  // System and tool turns are the pipeline talking to itself, not an answer to
  // the reader: never rendered (P1, no operational prose in the UI).
  if (message.role === "system" || message.role === "tool") {
    return null;
  }

  if (isPending) {
    return (
      <div
        className="flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-muted-foreground"
        data-testid={`analyst-message-${message.token}`}
      >
        <Loader2
          className="h-4 w-4 animate-spin text-primary"
          aria-hidden="true"
        />
        <span>{t("studyReport.analyst.runningCustomTable")}</span>
        <AnalystElapsed active startedAt={message.createdAt} />
      </div>
    );
  }

  if (isFailed) {
    return (
      <div
        className="flex flex-col gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4"
        data-testid={`analyst-message-${message.token}`}
      >
        <div className="flex items-start gap-2 text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {/* The failure code's localized line only: `errorMessage` is the
              server's diagnostic ("All report analyst retrieval paths
              failed") and stays in logs (P1). A row from before typed
              failure codes has none: the generic line. */}
          <span className="text-sm">
            {t(
              `studyReport.analyst.${
                message.failureCode
                  ? analystFailureCopyKey(message.failureCode)
                  : "analysisFailed"
              }` as "studyReport.analyst.analysisFailed",
            )}
          </span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => onRetry(message.requestToken)}
          data-testid={`analyst-retry-${message.token}`}
        >
          {t("studyReport.analyst.retry")}
        </Button>
      </div>
    );
  }

  if (message.role === "user") {
    return (
      // User turn markup copied verbatim from
      // apps/frontend/src/pages/ThreadDetailPage/ThreadDetailPage.tsx:2796
      // (the thread's own user-turn row). Per
      // docs/plans/analyst-thread-alignment.md Step 2: share the class
      // language, do not import from pages/ThreadDetailPage/*.
      <div
        className="ml-auto flex w-fit max-w-[80%] flex-col items-start gap-1.5"
        data-testid={`analyst-message-${message.token}`}
      >
        <div className="whitespace-pre-wrap break-words rounded-2xl bg-primary/10 px-4 py-3 text-base leading-[1.7] text-foreground">
          {message.content}
        </div>
      </div>
    );
  }

  const extraContent =
    numbering.placedFactIds.length > 0 ||
    message.charts.length > 0 ||
    tables.length > 0 ? (
      <div className="mt-4 flex flex-col gap-4">
        {tables.map((table, index) => (
          <AnalystTable
            key={`${message.token}-${table.title}-${index}`}
            table={table}
            index={index}
          />
        ))}
        {message.charts.map((chart, index) => (
          <AnalystChart
            key={`${message.token}-${chart.title}-${index}`}
            chart={chart}
            index={index}
          />
        ))}
        <AnalystEvidence
          evidence={message.evidence}
          messageToken={message.token}
          numbering={numbering}
          open={isEvidenceOpen}
          onOpenChange={setIsEvidenceOpen}
          focusFactId={focusFactId}
        />
      </div>
    ) : undefined;

  return (
    // Assistant turn: thread type scale, no bubble. Refusal is plain prose in
    // `text-chart-warning`; clarification is ordinary assistant prose. Neither
    // gets a border, a tint, or a sender label (docs/plans/analyst-thread-alignment.md
    // Step 2). `data-message-status` stays on the row for both.
    <div
      className="max-w-prose text-base leading-[1.7] text-foreground"
      data-testid={`analyst-message-${message.token}`}
      data-message-status={
        isRefused
          ? "refused"
          : isClarification
            ? "clarification"
            : message.status
      }
    >
      <SafeAnalystMarkdown
        content={message.content}
        numbering={numbering}
        onOpenFact={handleOpenFact}
        refused={isRefused}
        hideMarkdownTables={tables.length > 0}
        factsById={factsById}
      />
      {extraContent}
    </div>
  );
}
